<template>
  <div class="interactive-map-container">
    <!-- Header de la página -->
    <div class="d-flex justify-space-between align-center mb-6">
      <div>
        <h1 class="text-h4 font-weight-bold mb-2">
          <v-icon left color="primary" size="large">mdi-map</v-icon>
          Sistema de Mapas MGI
        </h1>
        <p class="text-body-1 text-grey-darken-1">
          Visualización geográfica en tiempo real de incidencias, supervisores y agencias
        </p>
      </div>
      <div class="d-flex gap-2">
        <v-btn
          color="success"
          prepend-icon="mdi-refresh"
          @click="refreshMapData"
          :loading="loading"
        >
          Actualizar Datos
        </v-btn>
        <v-btn
          color="primary"
          prepend-icon="mdi-fullscreen"
          @click="toggleFullscreen"
        >
          Pantalla Completa
        </v-btn>
      </div>
    </div>

    <!-- Estadísticas rápidas -->
    <MapStats 
      :stats="mapStats" 
      :loading="loading"
      class="mb-4"
    />

    <!-- Contenedor principal del mapa -->
    <v-row>
      <v-col cols="12" lg="9">
        <v-card elevation="6" class="map-card">
          <v-card-title class="bg-primary text-white d-flex align-center">
            <v-icon class="mr-2">mdi-map-marker-multiple</v-icon>
            Mapa Interactivo - República Dominicana
            <v-spacer></v-spacer>
            <v-chip 
              color="white" 
              text-color="primary" 
              size="small"
              prepend-icon="mdi-circle"
              :class="connectionStatus === 'connected' ? '' : 'pulse'"
            >
              {{ connectionStatus === 'connected' ? 'En línea' : 'Conectando...' }}
            </v-chip>
          </v-card-title>
          
          <v-card-text class="pa-0 position-relative">
            <!-- Controles del mapa con panel expandible y filtros avanzados -->
            <MapControls 
              :map="map"
              @layer-toggle="handleLayerToggle"
              @filter-change="handleFilterChange"
              @center-map="handleCenterMap"
              @focus-on="handleFocusOn"
              @refresh="handleDataRefresh"
              class="map-controls-overlay"
            />
            
            <!-- Contenedor del mapa Leaflet -->
            <div 
              id="map-container" 
              class="leaflet-map-container"
              :class="{ 'fullscreen': isFullscreen }"
            >
              <div v-if="mapError" class="map-error-overlay">
                <v-alert
                  type="error"
                  variant="tonal"
                  class="ma-4"
                >
                  <v-icon>mdi-map-marker-off</v-icon>
                  Error cargando el mapa: {{ mapError }}
                  <template v-slot:append>
                    <v-btn 
                      size="small" 
                      @click="initializeMap"
                      :loading="loading"
                    >
                      Reintentar
                    </v-btn>
                  </template>
                </v-alert>
              </div>
              
              <div v-if="loading && !mapInitialized" class="map-loading-overlay">
                <div class="text-center">
                  <v-progress-circular 
                    indeterminate 
                    color="primary" 
                    size="64"
                  ></v-progress-circular>
                  <p class="text-h6 mt-4">Cargando mapa...</p>
                  <p class="text-body-2 text-grey">Inicializando visualización geográfica</p>
                </div>
              </div>
            </div>
          </v-card-text>
        </v-card>
      </v-col>

      <!-- Panel lateral de información -->
      <v-col cols="12" lg="3">
        <!-- Leyenda del mapa -->
        <MapLegend 
          :layers="activeLayers"
          class="mb-4"
        />

        <!-- Panel de información contextual -->
        <v-card elevation="4" class="mb-4">
          <v-card-title class="bg-blue-grey-lighten-5">
            <v-icon class="mr-2">mdi-information</v-icon>
            Información del Mapa
          </v-card-title>
          <v-card-text>
            <v-list density="compact">
              <v-list-item>
                <v-list-item-title>Zoom Actual</v-list-item-title>
                <v-list-item-subtitle>{{ currentZoom }}</v-list-item-subtitle>
              </v-list-item>
              <v-list-item>
                <v-list-item-title>Centro</v-list-item-title>
                <v-list-item-subtitle>
                  {{ currentCenter.lat.toFixed(4) }}, {{ currentCenter.lng.toFixed(4) }}
                </v-list-item-subtitle>
              </v-list-item>
              <v-list-item>
                <v-list-item-title>Elementos Visibles</v-list-item-title>
                <v-list-item-subtitle>{{ visibleElements }} items</v-list-item-subtitle>
              </v-list-item>
              <v-list-item>
                <v-list-item-title>Última Actualización</v-list-item-title>
                <v-list-item-subtitle>{{ lastUpdate }}</v-list-item-subtitle>
              </v-list-item>
            </v-list>
          </v-card-text>
        </v-card>

        <!-- Acciones rápidas -->
        <v-card elevation="4">
          <v-card-title class="bg-green-lighten-5">
            <v-icon class="mr-2">mdi-lightning-bolt</v-icon>
            Acciones Rápidas
          </v-card-title>
          <v-card-text>
            <v-btn
              block
              color="primary"
              class="mb-2"
              prepend-icon="mdi-crosshairs-gps"
              @click="centerOnDominicanRepublic"
            >
              Centrar en RD
            </v-btn>
            <v-btn
              block
              color="orange"
              class="mb-2"
              prepend-icon="mdi-alert-circle"
              @click="focusOnIncidents"
            >
              Ver Incidencias
            </v-btn>
            <v-btn
              block
              color="green"
              class="mb-2"
              prepend-icon="mdi-account-supervisor"
              @click="focusOnSupervisors"
            >
              Ver Supervisores
            </v-btn>
            <v-btn
              block
              color="blue"
              prepend-icon="mdi-store"
              @click="focusOnAgencies"
            >
              Ver Agencias
            </v-btn>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, onUnmounted, computed, watch } from 'vue'
import { useMapData } from './composables/useMapData'
import { useMapLayers, type LayerVisibility } from './composables/useMapLayers'
import MapControls from './components/MapControls.vue'
import MapLegend from './components/MapLegend.vue'
import MapStats from './components/MapStats.vue'
// Carga dinámica de Leaflet
let L: typeof import('leaflet')
import { setLeafletModule } from './composables/useMapLayers'

// Configuración inicial del mapa para República Dominicana
const DOMINICAN_REPUBLIC_CENTER = {
  lat: 18.7357,
  lng: -70.1627
}
const DEFAULT_ZOOM = 8

// Estados reactivos
const map = ref<any | null>(null)
const mapInitialized = ref(false)
const loading = ref(true)
const mapError = ref<string | null>(null)
const isFullscreen = ref(false)
const connectionStatus = ref<'connected' | 'connecting'>('connecting')

// Estados del mapa
const currentZoom = ref(DEFAULT_ZOOM)
const currentCenter = ref(DOMINICAN_REPUBLIC_CENTER)
const visibleElements = ref(0)
const activeLayers = ref<string[]>(['incidents', 'supervisors'])

// Composables
const { 
  incidents, 
  supervisors, 
  agencies, 
  routes, 
  statistics,
  loadAllData, 
  refreshData 
} = useMapData()
const mapLayersComposable = ref<any>(null)

// Computed
const mapStats = computed(() => {
  // Usar las estadísticas directamente del composable
  if (!statistics?.value) {
    return {
      totalIncidents: 0,
      activeSupervisors: 0,
      totalAgencies: 0,
      activeRoutes: 0
    }
  }
  
  return {
    totalIncidents: statistics.value.totalIncidents || 0,
    activeSupervisors: statistics.value.activeSupervisors || 0,
    totalAgencies: statistics.value.totalAgencies || 0,
    activeRoutes: statistics.value.activeRoutes || 0
  }
})

const lastUpdate = computed(() => {
  return new Date().toLocaleTimeString('es-DO', {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit'
  })
})

// Métodos principales
const initializeMap = async () => {
  try {
    loading.value = true
    mapError.value = null

    // Cargar Leaflet y CSS dinámicamente y registrar en el composable
    const leafletModule = await import('leaflet')
    await import('leaflet/dist/leaflet.css')
    L = leafletModule
    setLeafletModule(leafletModule as any)

    // Configurar iconos por defecto de Leaflet
    delete (L.Icon.Default.prototype as any)._getIconUrl
    L.Icon.Default.mergeOptions({
      iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
      iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
      shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
    })

    // Esperar a que el DOM esté listo
    await new Promise(resolve => setTimeout(resolve, 100))

    // Crear el mapa
    map.value = L.map('map-container', {
      center: [DOMINICAN_REPUBLIC_CENTER.lat, DOMINICAN_REPUBLIC_CENTER.lng],
      zoom: DEFAULT_ZOOM,
      zoomControl: true,
      attributionControl: true,
      preferCanvas: true,
      // Evitar deslizamiento visual de marcadores
      markerZoomAnimation: false,
      zoomAnimation: true,
      fadeAnimation: true
    })

    // Agregar capa base
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '© OpenStreetMap contributors | Sistema MGI',
      maxZoom: 18,
      minZoom: 6,
      updateWhenZooming: false,
      updateWhenIdle: true
    }).addTo(map.value)

    // Configurar eventos del mapa
    map.value.on('zoomend', () => {
      updateMapInfo()
      try {
        // Forzar redraw de capas para eliminar cualquier desfase residual
        mapLayersComposable.value?.incidentLayers?.value?.eachLayer?.((l: any) => l.redraw?.())
        mapLayersComposable.value?.supervisorLayers?.value?.eachLayer?.((l: any) => l.redraw?.())
        mapLayersComposable.value?.agencyLayers?.value?.eachLayer?.((l: any) => l.redraw?.())
        mapLayersComposable.value?.routeLayers?.value?.eachLayer?.((l: any) => l.redraw?.())
      } catch {}
    })
    map.value.on('moveend', updateMapInfo)

    mapInitialized.value = true
    connectionStatus.value = 'connected'
    
    // Inicializar el composable de capas con la instancia del mapa PRIMERO
    mapLayersComposable.value = useMapLayers(map.value)

    // Inicializar las capas
    mapLayersComposable.value.initializeLayers()
    
    // Cargar datos iniciales DESPUÉS de inicializar las capas
    await loadAllData()
    
    // Poblar las capas con los datos cargados
    if (mapLayersComposable.value) {
      mapLayersComposable.value.updateIncidentLayer(incidents.value || [])
      mapLayersComposable.value.updateSupervisorLayer(supervisors.value || [])
      mapLayersComposable.value.updateAgencyLayer(agencies.value || [])
      mapLayersComposable.value.updateRouteLayer(routes.value || [])
    }
    
    console.log('✅ Mapa inicializado exitosamente')
    console.log('📊 Datos cargados:', {
      incidents: incidents.value?.length || 0,
      supervisors: supervisors.value?.length || 0,
      agencies: agencies.value?.length || 0,
      routes: routes.value?.length || 0
    })
    
  } catch (error) {
    console.error('❌ Error inicializando mapa:', error)
    mapError.value = error instanceof Error ? error.message : 'Error desconocido'
  } finally {
    loading.value = false
  }
}

const updateMapInfo = () => {
  if (map.value) {
    currentZoom.value = map.value.getZoom()
    const center = map.value.getCenter()
    currentCenter.value = {
      lat: center.lat,
      lng: center.lng
    }
  }
}

const refreshMapData = async () => {
  loading.value = true
  try {
    await refreshData()
    if (mapLayersComposable.value && map.value) {
      // Actualizar cada capa por separado
      mapLayersComposable.value.updateIncidentLayer(incidents.value || [])
      mapLayersComposable.value.updateSupervisorLayer(supervisors.value || [])
      mapLayersComposable.value.updateAgencyLayer(agencies.value || [])
      mapLayersComposable.value.updateRouteLayer(routes.value || [])
    }
    console.log('✅ Datos del mapa actualizados')
  } catch (error) {
    console.error('❌ Error actualizando datos:', error)
  } finally {
    loading.value = false
  }
}

const handleLayerChange = async (newLayers: string[]) => {
  if (map.value && mapInitialized.value && mapLayersComposable.value) {
    // Actualizar visibilidad de capas
    activeLayers.value = newLayers
    
    // Actualizar cada capa según la visibilidad
    if (newLayers.includes('incidents')) {
      mapLayersComposable.value.updateIncidentLayer(incidents.value || [])
    }
    if (newLayers.includes('supervisors')) {
      mapLayersComposable.value.updateSupervisorLayer(supervisors.value || [])
    }
    if (newLayers.includes('agencies')) {
      mapLayersComposable.value.updateAgencyLayer(agencies.value || [])
    }
    if (newLayers.includes('routes')) {
      mapLayersComposable.value.updateRouteLayer(routes.value || [])
    }
    
    console.log('✅ Capas actualizadas:', newLayers)
  }
}

// Nuevos métodos para controles avanzados
const handleLayerToggle = (layer: string, visible: boolean) => {
  console.log('🎯 Toggle layer:', layer, visible)
  
  // Actualizar la lista de capas activas
  if (visible) {
    if (!activeLayers.value.includes(layer)) {
      activeLayers.value.push(layer)
    }
  } else {
    activeLayers.value = activeLayers.value.filter(l => l !== layer)
  }
  
  // Usar el método correcto del composable para controlar la visibilidad
  if (map.value && mapInitialized.value && mapLayersComposable.value) {
    mapLayersComposable.value.setLayerVisibility(layer as keyof LayerVisibility, visible)
    console.log('🔧 Capa', layer, visible ? 'activada' : 'desactivada')
  }
}

const handleFilterChange = (filters: any) => {
  console.log('🔍 Filtros aplicados:', filters)
  
  // Aplicar filtros a través del composable
  if (map.value && mapInitialized.value && mapLayersComposable.value) {
    mapLayersComposable.value.updateLayerFilters(filters)
    
    // Actualizar las capas visibles con los nuevos filtros
    if (activeLayers.value.includes('incidents')) {
      mapLayersComposable.value.updateIncidentLayer(incidents.value || [])
    }
    if (activeLayers.value.includes('supervisors')) {
      mapLayersComposable.value.updateSupervisorLayer(supervisors.value || [])
    }
    if (activeLayers.value.includes('agencies')) {
      mapLayersComposable.value.updateAgencyLayer(agencies.value || [])
    }
    if (activeLayers.value.includes('routes')) {
      mapLayersComposable.value.updateRouteLayer(routes.value || [])
    }
  }
}

const handleCenterMap = (coordinates: [number, number], zoom = 12) => {
  console.log('🎯 Centrando mapa en:', coordinates, 'zoom:', zoom)
  
  if (map.value) {
    map.value.setView(coordinates, zoom, {
      animate: true,
      duration: 1
    })
  }
}

const handleFocusOn = (layerType: string) => {
  console.log('🎯 Focus on:', layerType)
  
  switch (layerType) {
    case 'incidents':
      focusOnIncidents()
      break
    case 'supervisors':
      focusOnSupervisors()
      break
    case 'agencies':
      focusOnAgencies()
      break
    default:
      console.warn('Unknown layer type for focus:', layerType)
  }
}

const handleDataRefresh = async () => {
  console.log('🔄 Refrescando datos del mapa...')
  await refreshMapData()
}

const toggleFullscreen = () => {
  isFullscreen.value = !isFullscreen.value
  // Invalidar el tamaño del mapa después del cambio
  setTimeout(() => {
    if (map.value) {
      map.value.invalidateSize()
    }
  }, 300)
}

// Métodos de navegación
const centerOnDominicanRepublic = () => {
  if (map.value) {
    map.value.setView([DOMINICAN_REPUBLIC_CENTER.lat, DOMINICAN_REPUBLIC_CENTER.lng], DEFAULT_ZOOM)
  }
}

const focusOnIncidents = () => {
  if (map.value && incidents.value?.length) {
    const group = new L.FeatureGroup()
    incidents.value.forEach((incident: any) => {
      if (incident.latitude && incident.longitude) {
        L.marker([incident.latitude, incident.longitude]).addTo(group)
      }
    })
    if (group.getLayers().length > 0) {
      map.value.fitBounds(group.getBounds())
    }
  }
}

const focusOnSupervisors = () => {
  if (map.value && supervisors.value?.length) {
    const group = new L.FeatureGroup()
    supervisors.value.forEach((supervisor: any) => {
      if (supervisor.currentLatitude && supervisor.currentLongitude) {
        L.marker([supervisor.currentLatitude, supervisor.currentLongitude]).addTo(group)
      }
    })
    if (group.getLayers().length > 0) {
      map.value.fitBounds(group.getBounds())
    }
  }
}

const focusOnAgencies = () => {
  if (map.value && agencies.value?.length) {
    const group = new L.FeatureGroup()
    agencies.value.forEach((agency: any) => {
      if (agency.latitude && agency.longitude) {
        L.marker([agency.latitude, agency.longitude]).addTo(group)
      }
    })
    if (group.getLayers().length > 0) {
      map.value.fitBounds(group.getBounds())
    }
  }
}

// Watcher para cambios en clustering - repoblar datos cuando cambie el tipo de capa
watch(() => mapLayersComposable.value?.layerVisibility.clustering, (newValue, oldValue) => {
  if (newValue !== oldValue && mapLayersComposable.value && incidents.value) {
    console.log('🔄 Clustering cambió, repoblando capa de incidencias...')
    // Dar tiempo para que se reconfigure la capa y luego repoblar
    setTimeout(() => {
      if (mapLayersComposable.value) {
        mapLayersComposable.value.updateIncidentLayer(incidents.value || [])
        console.log('✅ Capa de incidencias repoblada después del cambio de clustering')
      }
    }, 100)
  }
}, { immediate: false })

// Ciclo de vida
onMounted(async () => {
  await initializeMap()
})

onUnmounted(() => {
  if (map.value) {
    map.value.remove()
  }
})
</script>

<style scoped>
.interactive-map-container {
  min-height: 100vh;
  max-height: 100vh;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 24px;
}

.map-card {
  position: relative;
  min-height: 600px;
}

.leaflet-map-container {
  height: 600px;
  width: 100%;
  border-radius: 0 0 4px 4px;
  position: relative;
  z-index: 1;
}

.leaflet-map-container.fullscreen {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw !important;
  height: 100vh !important;
  z-index: 9999;
  border-radius: 0;
}

.map-controls-overlay {
  position: absolute;
  top: 10px;
  right: 10px;
  z-index: 1000;
}

.map-loading-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.9);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.map-error-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.pulse {
  animation: pulse 2s infinite;
}

@keyframes pulse {
  0% {
    opacity: 1;
  }
  50% {
    opacity: 0.5;
  }
  100% {
    opacity: 1;
  }
}

/* Estilos responsivos */
@media (max-width: 768px) {
  .interactive-map-container {
    padding: 16px;
  }
  
  .leaflet-map-container {
    height: 400px;
  }
}

/* Estilos para Leaflet */
:deep(.leaflet-container) {
  background: #f5f5f5;
  outline: none;
}

:deep(.leaflet-control-attribution) {
  background: rgba(255, 255, 255, 0.8);
  font-size: 11px;
}

:deep(.leaflet-popup-content-wrapper) {
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

:deep(.leaflet-popup-content) {
  margin: 12px 16px;
  line-height: 1.4;
}

/* Estabilidad de marcadores (evitar drift durante zoom) */
:deep(.custom-map-icon),
:deep(.leaflet-marker-icon) {
  will-change: top, left;
  transform: translateZ(0);
  transition: none !important;
}
</style>
